import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  getLeadFlowSettings,
  regenerateLeadFlowKey,
} from "../lib/api";
import {
  Copy, RefreshCw, Eye, EyeOff, CheckCircle, Zap, Link, Key,
  Loader2, ExternalLink, ShieldCheck, AlertCircle
} from "lucide-react";

export const LeadFlowPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(null); // "url" | "key"

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getLeadFlowSettings();
      setSettings(res.data);
    } catch (err) {
      toast.error("Impossible de charger la configuration LeadFlow");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
      toast.success("Copié dans le presse-papier");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm("Attention : l'ancienne clé sera invalidée immédiatement. LeadFlow ne pourra plus envoyer de leads jusqu'à ce que tu mettes à jour la clé dans les paramètres LeadFlow. Continuer ?")) return;
    try {
      setRegenerating(true);
      const res = await regenerateLeadFlowKey();
      toast.success("Nouvelle clé générée avec succès");
      // Reload settings to get new masked key
      await loadSettings();
      // Also show the new key immediately
      setSettings(prev => ({ ...prev, apiKeyFull: res.data.apiKeyFull }));
      setShowKey(true);
    } catch {
      toast.error("Erreur lors de la régénération de la clé");
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const webhookUrl = settings?.webhookUrl || "";
  const apiKey = settings?.apiKeyFull || "";
  const maskedKey = settings?.apiKeyMasked || "••••••••••••••••••••••••";

  return (
    <div className="space-y-6 p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
          <Zap className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Intégration LeadFlow</h1>
          <p className="text-sm text-muted-foreground">
            Recevez vos leads en temps réel depuis la plateforme LeadFlow
          </p>
        </div>
        <Badge variant="outline" className="ml-auto flex items-center gap-1 text-green-600 border-green-300 bg-green-50 dark:bg-green-900/20">
          <CheckCircle className="h-3 w-3" />
          Actif
        </Badge>
      </div>

      {/* Stats */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-indigo-100 dark:bg-indigo-900/30">
              <Zap className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-3xl font-bold">{settings?.totalLeadsReceived ?? 0}</p>
              <p className="text-sm text-muted-foreground">leads reçus via LeadFlow au total</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhook URL */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Link className="h-4 w-4" />
            URL Webhook CRM
          </CardTitle>
          <CardDescription>
            Colle cette URL dans le champ <strong>"URL Webhook CRM"</strong> des paramètres d'intégration sur LeadFlow.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <div
              data-testid="webhook-url-display"
              className="flex-1 font-mono text-sm bg-muted rounded-md px-3 py-2 break-all select-all border"
            >
              {webhookUrl}
            </div>
            <Button
              data-testid="copy-webhook-url-btn"
              variant="outline"
              size="icon"
              onClick={() => handleCopy(webhookUrl, "url")}
              title="Copier l'URL"
            >
              {copied === "url" ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* API Key */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Key className="h-4 w-4" />
            Clé API secrète
          </CardTitle>
          <CardDescription>
            Colle cette clé dans le champ <strong>"Clé API"</strong> des paramètres d'intégration sur LeadFlow.
            Garde-la confidentielle.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <div
              data-testid="api-key-display"
              className="flex-1 font-mono text-sm bg-muted rounded-md px-3 py-2 break-all border"
            >
              {showKey ? apiKey : maskedKey}
            </div>
            <Button
              data-testid="toggle-key-visibility-btn"
              variant="outline"
              size="icon"
              onClick={() => setShowKey(!showKey)}
              title={showKey ? "Masquer" : "Afficher"}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              data-testid="copy-api-key-btn"
              variant="outline"
              size="icon"
              onClick={() => handleCopy(apiKey, "key")}
              title="Copier la clé"
            >
              {copied === "key" ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>

          <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Si tu régénères la clé, pense à mettre à jour immédiatement la clé dans LeadFlow,
              sinon les leads ne seront plus reçus.
            </p>
          </div>

          <Button
            data-testid="regenerate-key-btn"
            variant="outline"
            onClick={handleRegenerate}
            disabled={regenerating}
            className="w-full"
          >
            {regenerating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Régénérer la clé API
          </Button>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" />
            Comment configurer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3 text-sm text-muted-foreground list-none">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-xs font-bold">1</span>
              <span>Sur LeadFlow, va dans <strong>Clients → fiche client → Intégration CRM</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-xs font-bold">2</span>
              <span>Active <strong>"Push CRM en temps réel"</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-xs font-bold">3</span>
              <span>Colle l'<strong>URL Webhook</strong> et la <strong>Clé API</strong> ci-dessus dans les champs correspondants</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-xs font-bold">4</span>
              <span>Clique sur <strong>"Tester la connexion"</strong> → doit afficher ✅ HTTP 200</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-xs font-bold">5</span>
              <span>Les leads arriveront automatiquement dans la <strong>campagne du même nom</strong> (créée automatiquement si elle n'existe pas encore)</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeadFlowPage;
